import { FC, useState, useRef, useEffect } from "react";

import { Box } from "@mui/material";
import dynamic from "next/dynamic";
import { useForm } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "../FormStepContainer";
import { shouldShowSkipStep } from "../helpers/shouldShowSkipStep";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { isRequired } from "@/common/helpers/validators";
import { useGoogleMapsAutocomplete } from "@/common/hooks/useGoogleMapsAutocomplete";
import { TicketingFrontendPageOutput } from "@/common/types/trpc";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Delete, Add as AddIcon } from "@/icons/outlined";

const LeafletMap = dynamic(() => import("@/components/LeafletMap/LeafletMap"), { ssr: false });

type CoordinatesOutput = TicketingFrontendPageOutput["coordinates"];

export type StepLocationFormData = {
  address: string | null;
  coordinates: CoordinatesOutput | null;
};

export const TicketingLiveEditorStepLocation: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const { logUserActionEvent, logUserInputEvent } = useLiveFormAmplitudeEvents();
  const { organization } = useCurrentUserContext();
  const formContainerRef = useRef<HTMLDivElement>(null);

  const [showAddressInputAndMap, setShowAddressInputAndMap] = useState(!!apiTicketing.address);

  const defaultValues: StepLocationFormData = {
    address: apiTicketing.address ?? null,
    coordinates:
      apiTicketing.coordinates.latitude || apiTicketing.coordinates.longitude
        ? {
            latitude: apiTicketing.coordinates.latitude,
            longitude: apiTicketing.coordinates.longitude,
          }
        : null,
  };
  const form = useForm<StepLocationFormData>({ defaultValues });

  const { addressString, addressCoordinates, hasAddress, resetAddressDataAndInput } = useGoogleMapsAutocomplete({
    defaultAddressCoordinates:
      apiTicketing.coordinates?.latitude && apiTicketing.coordinates?.longitude
        ? {
            lat: Number(apiTicketing.coordinates.latitude),
            lng: Number(apiTicketing.coordinates.longitude),
          }
        : undefined,
    defaultAddressString: apiTicketing.address ?? undefined,
    addressInputId: "address-input",
    organizationCountry: organization?.country,
    includeVenueName: true,
  });

  useEffect(() => {
    form.setValue("address", addressString ?? null, { shouldDirty: true });
    const latLng = addressCoordinates
      ? {
          latitude: addressCoordinates.lat,
          longitude: addressCoordinates.lng,
        }
      : null;
    form.setValue("coordinates", latLng, { shouldDirty: true });
  }, [addressString, addressCoordinates, form]);

  const updateTicketingLocation = trpc.updateTicketingLocation.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepLocationFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      return await updateTicketingLocation.mutateAsync({
        ticketingId: apiTicketing.id,
        address: formData.address,
        coordinates: formData.coordinates,
      });
    },
  });

  const onShowAddressClick = () => {
    setShowAddressInputAndMap(true);
    logUserActionEvent(AmplitudeEvents.FormAddressAdded);
  };

  const handleDeleteAddress = () => {
    resetAddressDataAndInput();
    form.setValue("address", null, { shouldDirty: true });
    form.setValue("coordinates", null, { shouldDirty: true });
    setShowAddressInputAndMap(false);
  };

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const addressInPreview = formData.address || t("dashboard", "liveFormEditor.address.placeholder");
      liveDataToMutate.ticketing.address = addressInPreview;
      liveDataToMutate.placeholders.isLocation = !formData.address;
    },
  });

  const wording: "event" | "custom" | "auction" = apiTicketing.formCategory.toLowerCase() as
    | "event"
    | "custom"
    | "auction";

  return (
    <FormStepContainer
      title={t("dashboard", `liveFormEditor.stepLocation.${wording}.title`)}
      form={form}
      description={t("dashboard", `liveFormEditor.stepLocation.${wording}.description`)}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        nextButtonLabel: shouldShowSkipStep(form) ? "skip" : "next",
      }}
    >
      {!showAddressInputAndMap && (
        <DashboardButton
          data-test="add-location-button"
          startIcon={<AddIcon />}
          variant="outlined"
          vibe="brand"
          onClick={onShowAddressClick}
        >
          {t("dashboard", `liveFormEditor.stepLocation.${wording}.addAddress`)}
        </DashboardButton>
      )}

      <LiveEditorBlock
        rowGap={2}
        ref={formContainerRef}
        sx={{
          visibility: showAddressInputAndMap ? "visible" : "hidden",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {/* Title */}
          <DashboardTypography variant="subtitle2">
            {t("dashboard", `liveFormEditor.stepLocation.${wording}.address`)}
          </DashboardTypography>

          {/* Delete button */}
          <DashboardIconButton
            data-test="delete-address-button"
            size="small"
            onClick={handleDeleteAddress}
            vibe="neutral"
          >
            <Delete color="error" />
          </DashboardIconButton>
        </Box>

        {/* Address input */}
        <DashboardTextField
          data-test="ticketing-address-input"
          control={form.control}
          name="address"
          id="address-input"
          placeholder={t("dashboard", `liveFormEditor.stepLocation.${wording}.addressPlaceholder`)}
          onBlur={logUserInputEvent({
            inputName: "address",
            isEmpty: !form.getValues("address")?.trim?.(),
            isValid: !form.formState.errors.address,
          })}
          rules={[isRequired(!!showAddressInputAndMap, t("common", "isRequired"))]}
          errorMessage={form.formState.errors.address?.message}
        />

        {hasAddress && addressCoordinates && (
          <LeafletMap data-test="ticketing-map" latitude={addressCoordinates.lat} longitude={addressCoordinates.lng} />
        )}
      </LiveEditorBlock>
    </FormStepContainer>
  );
};
